/**
 * Nested T-Hangar Layout Calculations — Interleaving/Zipper Pattern
 *
 * Key geometry:
 *   building_depth = 2 * wing_depth + tail_depth + 2 * wall_thickness
 *   pitch = unit_width + wall_thickness  (same-direction, non-alternating)
 *   building_width = (N-1) * pitch + unit_width + 2 * wall_thickness
 *
 *   Row A: N units, all south-facing, offset_y = 0
 *   Row B: N-1 units, all north-facing, offset_y = wing_depth, shifted pitch/2 in X
 *
 *   Depth zones (south to north):
 *     Row A wings  (wing_depth)
 *     Interleaving tails  (tail_depth) — Row A & B tails share this zone
 *     Row B wings  (wing_depth)
 *
 *   tail_depth defaults to wing_depth for backward compatibility.
 */

import type { NestedTHangarParams } from './params'
import { calculateNestedUnitPitch } from './params'
import { calculateStepWidth } from '../standard-t-hangar/params'

// ============================================================================
// CORE CALCULATIONS
// ============================================================================

/**
 * Building depth: 2 wing zones + 1 tail zone + 2 exterior walls
 * tail_depth defaults to wing_depth for backward compatibility
 */
export function calculateBuildingDepth(params: NestedTHangarParams): number {
  const tailDepth = params.tail_depth_ft ?? params.wing_depth_ft
  return 2 * params.wing_depth_ft + tailDepth + 2 * params.wall_thickness_ft
}

/**
 * Building width determined by Row A (the wider row with N units)
 */
export function calculateBuildingWidth(params: NestedTHangarParams): number {
  const pitch = calculateNestedUnitPitch(params)
  return (params.unit_count - 1) * pitch + params.unit_width_ft + 2 * params.wall_thickness_ft
}

/**
 * Row B unit count is always Row A - 1
 */
export function getRowBCount(params: NestedTHangarParams): number {
  return params.unit_count - 1
}

/**
 * Total units across both rows.
 * Base = 2N - 1. Each side in 'separate' mode adds 1 END unit.
 */
export function getTotalUnits(params: NestedTHangarParams): number {
  const base = 2 * params.unit_count - 1
  return base
    + (params.left_end_mode === 'separate' ? 1 : 0)
    + (params.right_end_mode === 'separate' ? 1 : 0)
}

/**
 * End-space gap dimensions (each side).
 * Width = pitch / 2, Depth = wing_depth
 */
export function calculateEndSpaceDimensions(params: NestedTHangarParams): { width: number; depth: number } {
  const pitch = calculateNestedUnitPitch(params)
  return {
    width: pitch / 2,
    depth: params.wing_depth_ft,
  }
}

/**
 * Calculate X offsets for Row A units.
 * Row A: wall + i * pitch
 */
export function calculateRowAOffsets(params: NestedTHangarParams): number[] {
  const pitch = calculateNestedUnitPitch(params)
  return Array.from({ length: params.unit_count }, (_, i) =>
    params.wall_thickness_ft + i * pitch
  )
}

/**
 * Calculate X offsets for Row B units.
 * Row B: wall + pitch/2 + j * pitch (shifted by half pitch)
 */
export function calculateRowBOffsets(params: NestedTHangarParams): number[] {
  const pitch = calculateNestedUnitPitch(params)
  const rowBCount = getRowBCount(params)
  return Array.from({ length: rowBCount }, (_, j) =>
    params.wall_thickness_ft + pitch / 2 + j * pitch
  )
}

// ============================================================================
// LAYOUT SUMMARY
// ============================================================================

export interface NestedTHangarLayoutResult {
  buildingWidth: number
  buildingDepth: number
  unitPitch: number
  stepWidth: number
  totalUnits: number
  rowBCount: number
  tShapeArea: number
  endSpaceWidth: number
  endSpaceDepth: number
  warnings: string[]
}

export function calculateNestedTHangarLayout(params: NestedTHangarParams): NestedTHangarLayoutResult {
  const buildingWidth = calculateBuildingWidth(params)
  const buildingDepth = calculateBuildingDepth(params)
  const unitPitch = calculateNestedUnitPitch(params)
  const stepWidth = calculateStepWidth(params)
  const rowBCount = getRowBCount(params)
  const totalUnits = getTotalUnits(params)
  const endSpace = calculateEndSpaceDimensions(params)
  const tailDepth = params.tail_depth_ft ?? params.wing_depth_ft
  const tShapeArea =
    params.unit_width_ft * params.wing_depth_ft +
    params.tail_width_ft * tailDepth

  const warnings: string[] = []

  if (params.tail_width_ft >= params.unit_width_ft) {
    warnings.push('Tail width must be less than unit width for proper T-shape')
  }

  if (2 * params.tail_width_ft > params.unit_width_ft) {
    warnings.push('Tail sections may overlap: 2 × tail_width > unit_width. Tighten tail or widen unit.')
  }

  if (stepWidth < 2) {
    warnings.push('Step width is very narrow (< 2 ft). Consider widening unit or narrowing tail.')
  }

  if (buildingWidth > 1000) {
    warnings.push('Building width exceeds 1000 ft. Consider reducing unit count.')
  }

  if (params.unit_count < 2) {
    warnings.push('At least 2 units required for interleaving pattern.')
  }

  return {
    buildingWidth,
    buildingDepth,
    unitPitch,
    stepWidth,
    totalUnits,
    rowBCount,
    tShapeArea,
    endSpaceWidth: endSpace.width,
    endSpaceDepth: endSpace.depth,
    warnings,
  }
}
